<template>
  <dialog ref="modalRef" class="modal" @close="onClose" @keydown.esc="onKeydownEscape" @mousedown="onMouseDown">
    <aside>
      <form v-if="showClosing" method="dialog">
        <button autofocus title="Fenster schließen">
          <PflegeIcon name="close-line" />
        </button>
      </form>
      <header class="h5">
        <slot name="heading">
          {{ heading }}
        </slot>
      </header>
      <div>
        <slot></slot>
      </div>
      <footer v-if="$slots.actions">
        <slot name="actions"></slot>
      </footer>
    </aside>
  </dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import PflegeIcon from '../Icon'

const showModal = defineModel({ type: Boolean, required: false, default: false })
const props = defineProps({
  showClosing: {
    type: Boolean,
    required: false,
    default: true,
  },
  isPersistent: {
    type: Boolean,
    required: false,
    default: false,
  },
  heading: {
    type: String,
    required: false,
    default: '',
  },
})

const modalRef = ref(null)
watch(showModal, (show) => {
  if (show) {
    modalRef.value.showModal()
  } else {
    modalRef.value.close()
  }
})
const onClose = () => {
  showModal.value = false
}
const onMouseDown = (e) => {
  if (!props.isPersistent && e.target === e.currentTarget) {
    // on backdrop click
    showModal.value = false
  }
}
const onKeydownEscape = (e) => {
  if (props.isPersistent) {
    e.preventDefault()
  }
}
</script>

<style scoped lang="scss">
dialog {
  --backdropBlurRadius: 0.25rem;
  --backdropColor: #fff2;
  --animationTime: var(--time1);
  width: min(100%, 40rem);
  box-shadow: 0 0.5rem 1rem #0005;
  padding: 1.33rem;
  border: 0;
  border-radius: 0.5rem;
  transition: display var(--animationTime) allow-discrete, overlay var(--animationTime) allow-discrete;

  @media (prefers-color-scheme: dark) {
    --backdropColor: #0002;
  }
}

@media (prefers-reduced-motion: no-preference) {
  dialog {
    animation: scale-down var(--animationTime) ease-in-out forwards;
  }

  dialog[open] {
    animation-name: scale-up;
  }
}

dialog[open]::backdrop {
  animation: blur-in var(--time2) ease-in-out forwards;
}

@supports (-moz-appearance: none) {
  dialog[open]::backdrop {
    background-color: var(--backdropColor);
    backdrop-filter: blur(var(--backdropBlurRadius));
  }
}

dialog aside {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

dialog aside > header:empty {
  display: none;
}

dialog aside > form {
  text-align: right;
  position: sticky;
  top: 0;
}

dialog aside > form button {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  display: inline-block;
}

dialog aside > footer {
  display: flex;
  gap: 1rem;
  align-items: center;
}

@media (width <= 430px) {
  dialog aside > footer {
    flex-direction: column;
  }
}

@keyframes blur-in {
  100% {
    background-color: var(--backdropColor);
    backdrop-filter: blur(var(--backdropBlurRadius));
  }
}

@keyframes scale-up {
  0% {
    scale: 0.9;
    opacity: 0;
  }

  50% {
    opacity: 0;
  }

  100% {
    scale: 1;
    opacity: 1;
  }
}

@keyframes scale-down {
  0% {
    scale: 1;
    opacity: 1;
  }

  50% {
    opacity: 1;
  }

  100% {
    scale: 0.9;
    opacity: 0;
  }
}
</style>
